import { z } from 'zod';
import { toUnits } from './allocation';

const DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const AMOUNT = /^\d{1,15}(\.\d{1,4})?$/;

/** A positive amount in the API's decimal format, with at most `decimals` fraction digits. */
export function amountField(decimals = 2) {
  return z
    .string()
    .trim()
    .min(1, 'Enter an amount')
    .refine((v) => AMOUNT.test(v), 'Enter a number like 1250 or 1250.50')
    .refine(
      (v) => (v.split('.')[1]?.length ?? 0) <= decimals,
      `At most ${String(decimals)} decimal places`,
    )
    .refine((v) => (toUnits(v) ?? 0n) > 0n, 'Must be more than zero');
}

export function paymentSchema(decimals = 2) {
  return z.object({
    contactId: z.string().min(1, 'Choose who the payment is with'),
    amount: amountField(decimals),
    methodId: z.string().min(1, 'Choose a payment method'),
    paymentDate: z.string().regex(DATE, 'Pick a date'),
    reference: z.string().trim().max(100, 'Keep it under 100 characters'),
    notes: z.string().trim().max(1000),
  });
}
export type PaymentFormValues = z.infer<ReturnType<typeof paymentSchema>>;

/** A refund pays out credit; it can't exceed the party's available credit. */
export function refundSchema(availableCredit: string, decimals = 2) {
  const max = toUnits(availableCredit) ?? 0n;
  return z.object({
    amount: amountField(decimals).refine(
      (v) => (toUnits(v) ?? 0n) <= max,
      'More than the available credit',
    ),
    methodId: z.string().min(1, 'Choose a payment method'),
    paymentDate: z.string().regex(DATE, 'Pick a date'),
    reference: z.string().trim().max(100, 'Keep it under 100 characters'),
  });
}
export type RefundFormValues = z.infer<ReturnType<typeof refundSchema>>;

export const methodSchema = z.object({
  name: z.string().trim().min(2, 'Give it a name').max(60),
  kind: z.enum(['CASH', 'BANK_TRANSFER', 'CARD', 'CHEQUE', 'OTHER']),
  accountId: z.string().min(1, 'Choose the account the money goes to'),
});
export type MethodFormValues = z.infer<typeof methodSchema>;
